/**
 * OverpricedMandatesWidget.jsx
 * =============================
 * Widget Cockpit Dirigeant — Mandats au-dessus du marché DVF
 *
 * Scanne les mandats actifs et affiche ceux dont le prix > médiane DVF + 10%.
 * Le manager peut agir directement (baisse de prix proposée data-driven).
 */
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import {
    Database, TrendingUp, AlertTriangle, Loader2, ArrowRight,
    Home, RefreshCw,
} from 'lucide-react';

function formatEUR(n) {
    if (n === null || n === undefined) return '—';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
}

export default function OverpricedMandatesWidget() {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [ran, setRan] = useState(false);

    const run = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.get('/dvf/overpriced-mandates');
            setData(res.data);
            setRan(true);
        } catch (e) {
            setError(e.response?.data?.detail || 'Erreur analyse');
        } finally {
            setLoading(false);
        }
    };

    const total = data?.total || 0;
    const list = data?.overpriced || [];

    return (
        <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative rounded-2xl overflow-hidden border border-blue-500/20 bg-gradient-to-br from-blue-950/30 via-card to-cyan-950/10"
            data-testid="dvf-overpriced-widget"
        >
            <div className="absolute top-0 right-0 w-48 h-48 bg-blue-500/10 rounded-full blur-[80px] pointer-events-none" />

            <div className="relative p-4 sm:p-6">
                <div className="flex items-start justify-between mb-4 gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shrink-0">
                            <Database className="w-5 h-5 text-white" />
                        </div>
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-[10px] text-blue-400 font-bold uppercase tracking-wider">Analyse DVF</p>
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 text-[9px] font-bold">
                                    Base officielle gov
                                </span>
                            </div>
                            <p className="text-sm sm:text-base font-bold text-foreground">
                                {ran ? `${total} mandat${total > 1 ? 's' : ''} au-dessus du marché` : 'Mandats au-dessus du marché DVF'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={run}
                        disabled={loading}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/30 hover:bg-blue-500/20 text-xs font-bold text-blue-300 transition-colors disabled:opacity-50"
                        data-testid="dvf-run-scan-btn"
                    >
                        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                        {loading ? 'Analyse…' : ran ? 'Rescanner' : 'Lancer l\'analyse'}
                    </button>
                </div>

                {!ran && !loading && (
                    <div className="text-center py-6">
                        <Database className="w-10 h-10 text-blue-400/50 mx-auto mb-2" />
                        <p className="text-xs text-muted-foreground max-w-sm mx-auto">
                            Croisez vos mandats actifs avec la base <span className="text-blue-300 font-bold">DVF officielle</span> (Etalab)
                            pour détecter les biens surévalués et proposer des baisses de prix data-driven.
                        </p>
                    </div>
                )}

                {error && (
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-300 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        {error}
                    </div>
                )}

                {ran && !loading && total === 0 && (
                    <div className="p-4 rounded-lg bg-green-500/5 border border-green-500/20 text-center">
                        <p className="text-sm font-bold text-green-400 mb-1">Portefeuille cohérent avec le marché</p>
                        <p className="text-xs text-muted-foreground">
                            Aucun mandat actif n'est plus de 10% au-dessus de la médiane DVF de son secteur.
                        </p>
                    </div>
                )}

                {list.length > 0 && (
                    <div className="space-y-2">
                        {list.slice(0, 5).map((m, i) => (
                            <motion.div
                                key={m.mandate_id || i}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: i * 0.05 }}
                                className="p-3 rounded-xl bg-black/30 border border-white/10 hover:border-red-500/30 transition-all group"
                                data-testid={`overpriced-mandate-${i}`}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <Home className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                            <p className="text-xs font-bold text-foreground truncate">{m.address}</p>
                                            {m.city && <span className="text-[10px] text-muted-foreground">· {m.city}</span>}
                                        </div>
                                        <div className="flex items-center gap-3 text-[10px] text-muted-foreground flex-wrap">
                                            <span>Prix affiché : <span className="text-foreground font-bold">{formatEUR(m.asking_price)}</span></span>
                                            <span>·</span>
                                            <span>Médiane secteur : <span className="text-blue-300 font-bold">{m.market_median_m2} €/m²</span></span>
                                            <span>·</span>
                                            <span className="text-[9px]">
                                                {m.sample_size} ventes analysées
                                            </span>
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-red-300 text-[10px] font-black">
                                            <TrendingUp className="w-3 h-3" />
                                            +{m.asking_vs_market_pct}%
                                        </div>
                                        {m.drop_suggested_pct && (
                                            <p className="text-[10px] text-amber-400 mt-1 font-bold">
                                                Baisse : −{m.drop_suggested_pct}%
                                            </p>
                                        )}
                                    </div>
                                </div>
                                {m.mandate_id && (
                                    <Link
                                        to={`/mandates/${m.mandate_id}`}
                                        className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold text-blue-400 hover:text-blue-300 transition-colors"
                                    >
                                        Voir le mandat <ArrowRight className="w-3 h-3" />
                                    </Link>
                                )}
                            </motion.div>
                        ))}
                        {list.length > 5 && (
                            <p className="text-[10px] text-center text-muted-foreground pt-1">
                                + {list.length - 5} autre{list.length - 5 > 1 ? 's' : ''} mandat{list.length - 5 > 1 ? 's' : ''} au-dessus du marché
                            </p>
                        )}
                    </div>
                )}

                <div className="mt-4 pt-3 border-t border-white/10">
                    <p className="text-[9px] text-muted-foreground">
                        Source : <span className="font-bold">DVF — Demandes de Valeurs Foncières</span> · data.gouv.fr / Etalab · Cache 30 jours
                    </p>
                </div>
            </div>
        </motion.div>
    );
}
